'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import moment from 'moment';

import RecipeCard from './SmartChoices/RecipeCard.react';
import ComboCard from './SmartChoices/ComboCard.react';
import ProductCard from './SmartChoices/ProductCard.react';
import Paginator from '../../Paginator.react';
import SearchKeywords from '../../Filters/SearchKeywords.react';
import Alert from '../../../Widgets/Alert/Alert.react';
import PostalCodeSelector from '../../../Groceries/PostalCodeSelector.react';

import UserActions from '@actions/UserActions';
import AuthStore from '@stores/AuthStore';
import UserStore from '@stores/UserStore';
import PlanStore from '@stores/PlanStore';
import MealStore from '@stores/MealStore';
import { updateCachedDocuments } from '@utils/Content';
import { getMealSearchParamsForProfile, setRecommendedModeSourceParameters } from '@pro/utils/Patients';
import Analytics from '@utils/Analytics';
import { getConfig } from '@utils/Env';
import { hasDeadlineExpired } from '@utils/Sunbasket';
import { getAssetsForMeals } from '@utils/Meals';
import { analyzePlan } from '@utils/Plans';
import { fetchDocumentsById } from '@utils/Content';

import './SmartChoices.scss';
import './Restaurants.scss';

export default class SmartChoices extends Component {
    static propTypes = {
        params: PropTypes.object,
        onChangeParams: PropTypes.func,
        searchTermsPlaceholder: PropTypes.string,
        mealType: PropTypes.object.isRequired,
    };

    static defaultProps = {
        searchTermsPlaceholder: 'SEARCH SMART CHOICES',
        searchTermsProp: 'terms',
    };

    static contextTypes = {
        profile: PropTypes.object,
        addSwapContext: PropTypes.object,
        recipes: PropTypes.object,
        details: PropTypes.object,
        foods: PropTypes.object,

        showFullBrowser: PropTypes.func,

        isPro: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        const { postal_code, preferences } = UserStore.getUser() || {};
        const { meal_kit_providers = null } = preferences;

        this.state = {
            lastParams: JSON.stringify(props.params),
            params: this.initializeFilters(props.params, props.mealType, preferences),
            results: [],
            fallbacks: [],
            total: 0,
            loading: false,
            loadingMore: false,
            dirty: false,
            mode: 'recipes',
            meal_kit_providers,
            postal_code,
            isModalOpen: false,
            preferences: preferences,
            capturePostalCodeWorking: false,
            capturePostalCodeAlert: null,
            analysisTimeoutId: null,
            analysisInProgress: false
        };

        this.debounceSearch = debounce(this.search, 750);
        this.planAnalysisCache = new Map();
    }

    _mounted = false;

    componentDidMount = async () => {
        this._mounted = true;

        await this.initializePlanAndAssets();

        if (!this.props.inhibitSearchOnMount) {
            this.search();
        }
    }

    initializePlanAndAssets = async () => {
        const { addSwapContext } = this.context;
        const plans = PlanStore.getPlans();

        const plan = addSwapContext.plan || plans.find(plan => {
            const start = moment(plan.date_start, 'YYYY-MM-DD');
            const end = moment(plan.date_end, 'YYYY-MM-DD');
            return addSwapContext.date && addSwapContext.date.isBetween(start, end, undefined, '[]');
        });

        if (!plan) {
            return;
        }

        if (addSwapContext.date && MealStore.isRangeLoaded(plan.date_start, plan.date_end)) {
            // Populate a synthetic meal plan from the meal store
            plan.items = MealStore.getMeals().filter(meal => {
                return moment(meal.date).isBetween(
                    moment(plan.date_start),
                    moment(plan.date_end),
                    undefined,
                    '[]'
                );
            });
        }

        // Check if meals for this date range are already loaded
        const assets = await getAssetsForMeals(plan.items);
        const analyzed = analyzePlan(plan, assets);

        // Clear analysis cache when plan/assets change
        this.planAnalysisCache.clear();

        this.setState({plan: analyzed.plan, assets});
    }

    componentWillUnmount = () => {
        this._mounted = false;
        // Clear any pending analysis timeout
        if (this.state.analysisTimeoutId) {
            clearTimeout(this.state.analysisTimeoutId);
        }
    }

    addExclusionFilters = (results, filters = {}) => {
        filters['!uuid'] = filters['!uuid'] || [];
        filters['!main_dish'] = filters['!main_dish'] || [];
        filters['!side_dish'] = filters['!side_dish'] || [];
        results.forEach(r => {
            if (r.type === 'recipe') {
                if (!filters['!uuid'].includes(r.uuid)) {
                    filters['!uuid'].push(r.uuid);
                }

                if (!filters['!main_dish'].includes(r.uuid)) {
                    filters['!main_dish'].push(r.uuid);
                }
            }

            if (r.type === 'combo') {
                if (!filters['!uuid'].includes(r.main_dish)) {
                    filters['!uuid'].push(r.main_dish);
                }

                if (!filters['!main_dish'].includes(r.main_dish)) {
                    filters['!main_dish'].push(r.main_dish);
                }

                if (!filters['!uuid'].includes(r.side_dish)) {
                    filters['!uuid'].push(r.side_dish);
                }

                if (!filters['!side_dish'].includes(r.side_dish)) {
                    filters['!side_dish'].push(r.side_dish);
                }
            }
        });

        return filters
    }

    appendSunbasketParams = async (params) => {
        const { addSwapContext } = this.context;
        const { postal_code } = this.state;

        const searchDate = moment(addSwapContext.date);

        const deliveryInfo = await AuthStore.fetch(
            getConfig('recipe_api') + '/sunbasket/zipcode-delivery-info?' + [
                'date=' + encodeURIComponent(addSwapContext.date.format('YYYY-MM-DD')),
                'postalCode=' + encodeURIComponent(postal_code),
            ].join('&')
        );

        if (!deliveryInfo.meal_provider_dates?.includes(searchDate.format('YYYY-MM-DD'))) {
            return params;
        }

        params.filters.store.push('sunbasket');
        params.filters.available_on = [
            'always',
            searchDate.format('YYYY-MM-DD'),
            searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
            searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
            searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
            searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
        ];

        params.filters.available_on = params.filters.available_on.filter((date)=> !hasDeadlineExpired(date));

        // Don't recommend us stuff that's not available
        params.filters['!food_uuid'] = (params.filters['!food_uuid'] || []).concat(deliveryInfo?.unavailable || []);

        return params;
    }

    initializeFilters = (params, mealType) => {
        params.filters["avg_cost_per_serving"] = mealType?.max_cost;
        params.filters['!tags'] = params.filters['!tags'] || [];
        params.filters['!tags'].push('Ready Made Meal');
        return params;
    }

    prepareFilters = async (mode, params) => {
        const { isPro } = this.context;

        if (mode == 'recipes') {
            params.filters['!tags'] = params.filters['!tags'] || [];
            params.filters['!tags'].push('Ready Made Meal');
        }


        if (['meal-kits', 'ready-made-meals'].includes(mode)) {
            const { meal_kit_providers = [] } = this.state;
            params.types = ['product'];
            params.filters.store = ['universal'];
            params.filters.product_type = mode ==  'meal-kits' ? 'Meal Kit' : 'Ready Made Meal';

            // Remove "Ready Made Meal" from exclusions when searching for ready-made meals
            if (mode === 'ready-made-meals' && params.filters['!tags']) {
                params.filters['!tags'] = params.filters['!tags'].filter(tag => tag !== 'Ready Made Meal');
            }

            if (!isPro && mode === 'meal-kits') {
                 for (const i in meal_kit_providers) {
                    if (meal_kit_providers[i] === 'sunbasket') {
                        params = await this.appendSunbasketParams(params);
                    }

                    // @todo add more providers here later
                }
            }
        }

        return params;
    }

    setMode = async (mode) => {
        this.setState({loading: true});
        const { postal_code } = this.state;

        const isModalOpen = ['meal-kits', 'ready-made-meals'].includes(mode) && !postal_code?.length;

        if (isModalOpen) {
            this.setState({mode, isModalOpen});
        } else {
            const params = await this.prepareFilters(mode, JSON.parse(JSON.stringify(this.props.params)));
            this.setState({mode, params, isModalOpen, capturePostalCodeWorking: false, capturePostalCodeAlert: null}, this.debounceSearch);
        }
    }

    search = async () => {
        const { addSwapContext } = this.context;
        const { mealType } = this.props;
        const { mode, params } = this.state;
        params.size = 3;

        // Clear analysis cache when new search starts
        this.planAnalysisCache.clear();

        // Cancel any existing analysis
        if (this.state.analysisTimeoutId) {
            clearTimeout(this.state.analysisTimeoutId);
        }

        this.setState({loading: true, dirty: true, results: [], fallbacks: [], suggestions: null, total: 0, fallbackTotal: 0, analysisTimeoutId: null, analysisInProgress: false});

        const url = mode == 'recipes' ? '/search-uniques' : '/search-products';
        let results;

        try {
            results = await AuthStore.fetch(getConfig('recipe_api') + url, {
                method: 'POST',
                headers: {'Content-Type': 'application/json; schema=search/advanced/1'},
                body: JSON.stringify(params),
            }, true);

            // Courtesy update of in-memory caching
            if (results && results.elements) {
                updateCachedDocuments(results.elements);
            }

            if (results.elements.length == 0) {
                return this.searchFallback();
            }

            Analytics.searchSmartChoices({
                "Mode": mode,
                "Date": moment(addSwapContext.date) ?? null,
                "Meal Type": addSwapContext.mealType,
                "Total Results": results.total > 6 ? results.total : results.elements.length,
            });

            this.setState({
                results: results.elements,
                fallbacks: [],
                suggestion: results.suggestion,
                total: results.total > 6 ? results.total : results.elements.length,
                fallbackTotal: 0,
                resultsParams: params,
                loading: false,
            }, () => {
                // Start efficiency analysis after results are set
                this.startAnalysis();
            });

        } catch(error) {
            return;
        } finally {
            this.setState({loading: false, total: 0});
        }

        // Courtesy update of in-memory caching
        if (results && results.elements) {
            updateCachedDocuments(results.elements);
        }

        if (results.elements.length == 0) {
            return this.searchFallback();
        }

        Analytics.searchSmartChoices({
            "Mode": mode,
            "Date": moment(addSwapContext.date) ?? null,
            "Meal Type": mealType.name,
            "Total Results": results.total > 6 ? results.total : results.elements.length,
        });

        this.setState({
            results: results.elements,
            fallbacks: [],
            suggestion: results.suggestion,
            total: results.total > 6 ? results.total : results.elements.length,
            fallbackTotal: 0,
            resultsParams: params,
            loading: false,
        });

    }

    showMore = () => {
        const { results, params, loadingMore, mode } = this.state;

        if (loadingMore) {
            return;
        }

        if (results.length >= 500) {
            return;
        }

        this.addExclusionFilters(results, params.filters);

        this.setState({loadingMore: true});


        Analytics.addSwapShowMore({'Search Tool': 'Smart Choices'});

        const url = mode == 'recipes' ? '/search-uniques' : '/search-products';

        AuthStore.fetch(getConfig('recipe_api') + url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json; schema=search/advanced/1'},
            body: JSON.stringify({
                ...params,
                size: 6,
            }),
        }, true).then(data => {

            // Courtesy update of in-memory caching
            if (data && data.elements) {
                updateCachedDocuments(data.elements);
            }

            const newResults = results.concat(data.elements);

            this.setState({
                results: newResults,
                total: data.total > 6 ? this.state.total : newResults.length,
                loading: false,
                loadingMore: false,
            }, () => {
                // Start efficiency analysis after more results are added
                this.startAnalysis();
            });
        }, (error) => this.setState({ loading: false, loadingMore: false, total: 0 }));
    }

    processUnanalyzedResults = async () => {
        if (!this._mounted) {
            return;
        }

        const { results, fallbacks } = this.state;

        // Find first unanalyzed result
        const allResults = [...results, ...fallbacks];
        const unanalyzedResult = allResults.find(r =>
            r.efficiency_delta === undefined && r.efficiency_absolute === undefined
        );

        if (!unanalyzedResult) {
            // All done, clear timeout reference
            this.setState({ analysisTimeoutId: null, analysisInProgress: false });
            return;
        }

        // Analyze one result
        const efficiency = await this.calculateResultEfficiency(unanalyzedResult);
        if (efficiency) {
            // Spread efficiency data into the result
            Object.assign(unanalyzedResult, efficiency);

            // Trigger re-render with updated results
            if (this._mounted) {
                this.setState({
                    results: [...this.state.results],
                    fallbacks: [...this.state.fallbacks]
                });
            }
        }

        // Schedule next analysis if component is still mounted
        if (this._mounted) {
            const timeoutId = setTimeout(() => this.processUnanalyzedResults(), 100);
            this.setState({ analysisTimeoutId: timeoutId });
        }
    }

    startAnalysis = () => {
        // Cancel any existing analysis
        if (this.state.analysisTimeoutId) {
            clearTimeout(this.state.analysisTimeoutId);
        }

        // Start new analysis
        this.setState({ analysisInProgress: true });
        const timeoutId = setTimeout(() => this.processUnanalyzedResults(), 100);
        this.setState({ analysisTimeoutId: timeoutId });
    }

    searchFallback = () => {
        const { addSwapContext, profile } = this.context;
        const { mealType } = this.props;
        const { mode } = this.state;

        // make up our own params, inherit from the main params
        const params = {
            ...this.state.params,
            ...getMealSearchParamsForProfile(mealType, profile)
        };

        params.types = this.state.params.types;
        params.filters.tags = this.state.params.filters.tags;
        params.filters['!tags'] = this.state.params.filters['!tags'];
        params.filters['!uuid'] = this.state.params.filters['!uuid'];
        params.filters['store'] = this.state.params.filters['store'];
        params.filters['product_type'] = this.state.params.filters['product_type'];
        params.filters["avg_cost_per_serving"] = mealType?.max_cost;
        params.size = 3;

        setRecommendedModeSourceParameters('smart_choices', params, profile);

        const url = mode == 'recipes' ? '/search-uniques' : '/search-products';

        if (mode === 'meal-kits') {
            const searchDate = moment(addSwapContext.date);

            params.filters.available_on = [
                'always',
                searchDate.format('YYYY-MM-DD'),
                searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
                searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
                searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
                searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
            ];
        }

        AuthStore.fetch(getConfig('recipe_api') + url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json; schema=search/advanced/1'},
            body: JSON.stringify(params),
        }, true).then(results => {
            // Courtesy update of in-memory caching
            if (results && results.elements) {
                updateCachedDocuments(results.elements);
            }

            Analytics.searchSmartChoices({
                "Mode": mode,
                "Date": moment(addSwapContext.date) ?? null,
                "Meal Type": mealType.name,
                "Total Results": results.total > 6 ? results.total : results.elements.length,
                fallback: true
            });

            this.setState({
                results: [],
                total: 0,
                fallbacks: results.elements,
                fallbackTotal: results.total > 6 ? results.total : results.elements.length,
                fallbackParams: params,
                loading: false,
            }, () => {
                // Start efficiency analysis after fallback results are set
                this.startAnalysis();
            });

        }, (error) => this.setState({loading: false, fallbackTotal: 0}));
    }

    showMoreFallbacks = () => {
        const { addSwapContext } = this.context;
        const { fallbacks, fallbackParams, loadingMore, mode } = this.state;

        if (loadingMore) {
            return;
        }

        if (fallbacks.length >= 500) {
            return;
        }

        this.addExclusionFilters(fallbacks, fallbackParams.filters);

        this.setState({loadingMore: true});

        Analytics.addSwapShowMore({'Search Tool': 'Smart Choices'});

        const url = mode == 'recipes' ? '/search-uniques' : '/search-products';

        if (mode === 'meal-kits') {
            const searchDate = moment(addSwapContext.date);

            fallbackParams.filters.available_on = [
                'always',
                searchDate.format('YYYY-MM-DD'),
                searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
                searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
                searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
                searchDate.subtract(1, 'day').format('YYYY-MM-DD'),
            ];
        }

        AuthStore.fetch(getConfig('recipe_api') + url, {
            method: 'POST',
            headers: {'Content-Type': 'application/json; schema=search/advanced/1'},
            body: JSON.stringify({
                ...fallbackParams,
                size: 6,
            }),
        }, true).then(data => {

            // Courtesy update of in-memory caching
            if (data && data.elements) {
                updateCachedDocuments(data.elements);
            }

            const newFallbacks = fallbacks.concat(data.elements);

            this.setState({
                fallbacks: newFallbacks,
                fallbackTotal: data.total > 6 ? this.state.fallbackTotal : newFallbacks.length,
                loading: false,
                loadingMore: false,
            }, () => {
                // Start efficiency analysis after more fallback results are added
                this.startAnalysis();
            });
        }, (error) => this.setState({ loading: false, loadingMore: false, fallbackTotal: 0 }));

    }

    onChangeParams = (params) => {
        this.setState({params}, this.debounceSearch);
    }

    switchToFullbrowser = () => {
        const { params } = this.state;
        const { showFullBrowser } = this.context;

        showFullBrowser({
            defaultTerms: params.terms,
            defaultTypes: ['recipe'],
            inhibitSearchOnMount: false,
        });
    }

    calculateResultEfficiency = async (result) => {
        const { addSwapContext } = this.context;
        const { plan } = this.state;

        // If we don't have a plan with meals, we can't calculate efficiency
        if (!plan?.items?.length || !addSwapContext) {
            return null;
        }

        // Create a cache key based on the plan and result
        const cacheKey = [
            plan.uuid,
            result.uuid,
            result.type,
            addSwapContext.date ? addSwapContext.date.format('YYYY-MM-DD') : addSwapContext.offset,
            addSwapContext.mealType
        ].join('|');

        // Check cache first
        if (this.planAnalysisCache.has(cacheKey)) {
            return this.planAnalysisCache.get(cacheKey);
        }

        const { date, offset, mealType, replaceMeals = [] } = addSwapContext;

        // Find the meals for the current date and meal type
        const replaceMealIds = replaceMeals.map(m => m.id || m.uuid);

        // Create a copy of the plan with the swapped meal
        const modifiedPlan = {
            ...plan,
            items: plan.items.filter(item => {
                const id = item.id || item.uuid;
                return !replaceMealIds.includes(id);
            })
        };

        // Add the new item based on result type
        if (result.type === 'recipe') {
            modifiedPlan.items.push({
                recipe_uuid: result.uuid,
                details_uuid: result.details,
                meal_type: 'fresh',
                meal: mealType,
                offset,
                date: date ? date.format('YYYY-MM-DD') : null,
            });
        } else if (result.type === 'combo') {
            const pair = await fetchDocumentsById([result.main_dish, result.side_dish]);

            pair.forEach(c => {
                // Add both main and side dishes
                modifiedPlan.items.push({
                    recipe_uuid: c.uuid,
                    details_uuid: c.details,
                    meal_type: 'fresh',
                    meal: mealType,
                    offset,
                    date: date ? date.format('YYYY-MM-DD') : null,
                });
            });
        } else if (result.type === 'food') {
            // Products might have a different structure
            modifiedPlan.items.push({
                food_uuid: result.uuid,
                meal_type: 'food',
                meal: mealType,
                offset,
                date: date ? date.format('YYYY-MM-DD') : null,
                is_product: true
            });
        }

        // Analyze the modified plan
        const assets = await getAssetsForMeals(modifiedPlan.items);
        const analyzed = analyzePlan(modifiedPlan, assets);

        const returnResult = {
            efficiency_absolute: analyzed.plan.efficiency_score,
            efficiency_delta: analyzed.plan.efficiency_score - plan.efficiency_score,
        };

        // Cache the returnResult
        this.planAnalysisCache.set(cacheKey, returnResult);

        return returnResult;
    }

    renderResult = (result, key) => {
        const { params } = this.state;
        const { addSwapContext } = this.context;

        if (result.type === 'recipe') {
            return <RecipeCard key={key} searchRanking={key + 1} recipe={result} params={params}
                efficiencyDelta={result.efficiency_delta}
                efficiencyAbsolute={result.efficiency_absolute}
                />
        }

        if (result.type === 'combo') {
            return <ComboCard key={key} searchRanking={key + 1} combo={result} params={params}
                efficiencyDelta={result.efficiency_delta}
                efficiencyAbsolute={result.efficiency_absolute}
                />
        }

        if (result.type == 'product') {
            return <ProductCard key={key} searchRanking={key + 1} product={result} params={params}
                efficiencyDelta={result.efficiency_delta}
                efficiencyAbsolute={result.efficiency_absolute}
                />
        }
    }

    renderModeSelectorButtons = () => {
        const { mode, meal_kit_providers } = this.state;
        const { mealType } = this.props;
        const { isPro, profile } = this.context;
        const { inhibit_add_swap_modes } = profile;
        const modePicker = [];

        if(!(inhibit_add_swap_modes?.includes('smart-choices::meal-kits') && inhibit_add_swap_modes?.includes('smart-choices::ready-made'))) {
            modePicker.push(
                <li key="recipes">
                    <button key="recipes" data-active={mode === "recipes"} onClick={() => this.setMode('recipes')}>Recipes</button>
                </li>
            );
        }

        if (meal_kit_providers?.length && !["Breakfast", "Snack"].includes(mealType.main_dish) && !isPro && !inhibit_add_swap_modes?.includes('smart-choices::meal-kits')) {
            modePicker.push(
                <li key="meal-kits">
                    <button key="meal-kits" data-active={mode === "meal-kits"} onClick={() => this.setMode('meal-kits')}>Meal Kits</button>
                </li>
            );
        }

        if(!inhibit_add_swap_modes?.includes('smart-choices::ready-made')) {
            modePicker.push(
                <li key="ready-made-meals">
                    <button key="ready-made-meals" data-active={mode === "ready-made-meals"} onClick={() => this.setMode('ready-made-meals')}>Ready Made Meals</button>
                </li>
            );
        }

        if (modePicker.length <= 1) {
            return;
        }

        return <ul className="mode-picker">{modePicker}</ul>
    }


    onChangePostalCode =  (ev) => {
        const postalCode = ev.target.value;
        this.setState({postal_code: postalCode});
    }

    onSavePostalCode = async() => {
        const { postal_code, mode } = this.state;
        UserActions.updateSpecificMeta({postal_code});

        if (!postal_code.length) {
            this.setState({capturePostalCodeAlert: 'Please enter your postal code'});
            return;
        }

        this.setState({capturePostalCodeWorking: true});

        const response = await AuthStore.fetch({url: getConfig('recipe_api') + '/providers?postal_code=' + (postal_code || '')});
        const sunbasket = response?.elements.find(pv => pv.provider_key === 'sunbasket' && pv.available);

        if (!sunbasket) {
            this.setState({
                capturePostalCodeWorking: false,
                capturePostalCodeAlert: 'Very sorry but Sunbasket delivery is not available for your postal code.'
            });

            return;
        }

        this.setMode(mode)
    }

    closeModal = () => {
        const {  preferences } = this.state;

        preferences.meal_kit_providers = [];

        UserActions.updateSpecificMeta({preferences});

        this.setState({isModalOpen: false}, ()=> this.setMode('recipes'))
    }

    renderZipCodeModal = () => {
        const { isModalOpen, postal_code, capturePostalCodeWorking, capturePostalCodeAlert } = this.state;

        if (!isModalOpen) {
            return null;
        }

        return(
            <PostalCodeSelector isModalOpen
                postalCode={postal_code}
                working={capturePostalCodeWorking}
                closeModal={this.closeModal}
                onSavePostalCode={this.onSavePostalCode}
                alertMsg={capturePostalCodeAlert}
                modalTitle="Confirm Delivery Postal Code"
                saveButtonText="SUBMIT POSTAL CODE"
                onChangePostalCode={this.onChangePostalCode}>

                <p className="p3">To receive personalized recommendations for ready-made meals and meal kits, please enter your postal code below.</p>
            </PostalCodeSelector>

        )
    }


    render() {
        const { searchTermsProp, searchTermsPlaceholder, mealType } = this.props;
        const { params, fallbacks, loading, total, fallbackTotal, loadingMore, results, resultsParams,
                fallbackParams, dirty, meal_kit_providers, mode, postal_code } = this.state;
        const { isPro, profile } = this.context;
        const { inhibit_add_swap_modes = [] } = profile;

        const breakfastSnackRmmTotalFailureMessage = isPro && profile.first_name ?
                <span><strong>Ready-Made Coming Soon! </strong>{`We’re sorry, ready-made meals matching ${profile.first_name}’s ${mealType?.name?.toLowerCase()} prescription are not yet available. We’ll notify you once they become available.`}</span>
                : <span><strong>Ready-Made Coming Soon! </strong>{`We’re sorry, ready-made meals matching your ${mealType?.name?.toLowerCase()} prescription are not yet available. We’ll notify you once they become available.`}</span>

        const isTotalFailure = (dirty && !loading && total === 0 && fallbackTotal === 0);

        const totalFailureMessages = {
            "recipes": <span>Sorry, we couldn’t find anything that fits.</span>,
            "ready-made-meals": isPro && profile.first_name ?
                <span><strong>Ready-Made Not Available: </strong>{`Ready-made meals matching ${profile.first_name}’s ${mealType?.name?.toLowerCase()} nutrition prescription could not be found.`}</span>
                : <span><strong>Ready-Made Not Available: </strong>{`Ready-made meals matching your ${mealType?.name?.toLowerCase()} nutrition prescription could not be found.`}</span>,
            "meal-kits": isPro && profile.first_name ?
                <span><strong>Meal Kits Not Available: </strong>{`Meal Kits matching ${profile.first_name}’s ${mealType?.name?.toLowerCase()} nutrition prescription are not available for this particular day. Availability is dependent upon meal kit provider menu schedules and delivery options.`}</span>
                : <span><strong>Meal Kits Not Available: </strong>{`Meal Kits matching your ${mealType?.name?.toLowerCase()} nutrition prescription are not available for this particular day. Availability is dependent upon meal kit provider menu schedules and delivery options.`}</span>
        }

        return (
            <div className="smart-choices-picker">
                <header data-dirty={dirty}>
                    <SearchKeywords params={params}
                        termsProp={searchTermsProp}
                        className="recipe-keywords-search ingredient-search"
                        onChangeParams={this.onChangeParams}
                        placeholder={searchTermsPlaceholder} />

                    {dirty && !loading ? <>
                        {!fallbacks?.length
                            ? <h3>Select from these <strong>Smart Choices</strong> that fit your nutrition requirements.</h3>
                            : <Alert className="alert-smart-choices" type="question" iconType="icon-warning5"
                                description={<><strong>A perfect match couldn't be found</strong>, but here are some options that are close enough.</>}
                                hintDescription={<p>Our Lena Nutrition Intelligence technology looks at 5 million data points to offer up meal choices that fit your nutrition prescription in the most perfect way, but sometimes even Lena can't find a perfect match when considering other foods you have scheduled for the day. The meals shown bring you close enough to meeting the requirements of your nutrition prescription. You may see warning signs appear when these meals are scheduled. Hovering over the warning sign will show you nutrient details to keep you informed.</p>} />
                        }
                    </> : null}

                    {!dirty ?
                        <h3>Enter up to 3 ingredients<br />
                        Eatlove will tell you what to make with them!</h3>
                    : null}

                    {!loading && (!["Breakfast", "Snack"].includes(mealType.main_dish) || !isPro) ?
                        this.renderModeSelectorButtons()
                    : null}
                </header>

                {loading ?
                    <div className="smart-choices-picker-loading">
                        <h2>Loading...</h2>
                        <i className="icon-spinner" />
                    </div>
                : null}

                {dirty && !isTotalFailure && !loading ?
                    <div className="results-list">
                        {results.map(this.renderResult)}
                        {fallbacks.map(this.renderResult)}
                    </div>
                : null}

                {isTotalFailure ?
                    <div className="smart-choices-question">
                        <Alert type="question"
                            iconType="no-icon"
                            description={<>{["Breakfast", "Snack"].includes(mealType) && mode == "ready-made-meals" ? breakfastSnackRmmTotalFailureMessage : totalFailureMessages[mode]}</>}
                            hintDescription={<p>Our Lena Nutrition Intelligence technology looks at 5 million data points to offer up meal choices that fit your nutrition prescription in the most perfect way, but sometimes even Lena can't find a perfect match when considering other foods you have scheduled for the day. The meals shown bring you close enough to meeting the requirements of your nutrition prescription. You may see warning signs appear when these meals are scheduled. Hovering over the warning sign will show you nutrient details to keep you informed.</p>}
                        />
                    </div>
                : null}

                <footer>
                    {total > 0 ?
                        <Paginator loading={loading}
                            loadingMore={loadingMore}
                            total={total}
                            results={results}
                            resultsParams={resultsParams}
                            showMore={this.showMore}
                            buttonText={loadingMore || loading ? "Loading..." : "Load More Smart Choices"} />
                    : null}

                    {fallbackTotal > 0 ?
                        <Paginator loading={loading}
                            loadingMore={loadingMore}
                            total={fallbackTotal}
                            results={fallbacks}
                            resultsParams={fallbackParams}
                            showMore={this.showMoreFallbacks}
                            buttonText={loadingMore || loading ? "Loading..." : "Load More Smart Choices"} />
                    : null}

                    {params.terms && !loading && !inhibit_add_swap_modes.includes('browser') ?
                        <button className="sub-action-btn" onClick={this.switchToFullbrowser}>
                            Not finding what you’re looking for in Smart Choices? <em>Search all recipes</em>
                        </button>
                    : null}

                    {!loading && results.length === total && fallbacks.length === fallbackTotal && !params.terms && !inhibit_add_swap_modes.includes('browser') ?
                        <button className="sub-action-btn" onClick={this.switchToFullbrowser}>
                            Not finding what you’re looking for in Smart Choices? <em>Search all recipes</em>
                        </button>
                    : null}
                </footer>
                {this.renderZipCodeModal()}
            </div>
        );
    }
}
